import { createSelector } from "@reduxjs/toolkit";
import { assertArrayIsDefined } from "../util/assert";
import { naturalSort, naturalSortInPlace } from "../util/sort";
import { LogInProcessState } from "./auth";
import { CustomizationObjectType, CustomizationTreeViewDataItem, DEFAULT_FILENAME, METADATA_FILENAME_ATTRIBUTE, RoiCustomization } from "./model-customization-types";
import { aeTitleRuleAdapterSelectors, customizationBaseAdapterSelectors, customizationOutputAdapterSelectors, customizationValidationErrorAdapterSelectors, daemonValidationErrorAdapterSelectors, dicomAttributeRuleAdapterSelectors, dicomRuleAdapterSelectors, globalRoiCustomizationAdapterSelectors, modelAdapterSelectors, modelCustomizationsMetadataAdapterSelectors, originalCustomizationsMetadataAdapterSelectors, originalRoiCustomizationAdapterSelectors, roiCustomizationAdapterSelectors, StoreState } from "./reducers";
import { TextEditorFile } from "./text-editor-file";

// A couple of complex types just to help visual studio code/tsc handle more complex selectors. These are not needed outside this file.
// type Selector<S> = (state: StoreState) => S;
type SelectorWithInputArg<S, T> = (state: StoreState, input: T) => S;

export const getLatestTextEditorFile = (state: StoreState, staleFile: TextEditorFile) => state.textEditorFiles.find(f => f.filePath === staleFile.filePath);

/** Returns app auth states from store that are required but for which the login process has not yet been started. */
export const getAppAuthStatesThatNeedLogin = (state: StoreState) => state.appAuthStates.filter(a => a.isRequired && a.logInProcessState === LogInProcessState.NotLoggedIn);

/** Returns true if at least one required app auth has been logged into, false otherwise. */
export const getIsLoggedIntoAtLeastOneAppAuth = (state: StoreState) => state.appAuthStates.some(a => a.isRequired && a.logInProcessState === LogInProcessState.LoggedIn);

/** Returns current cloud backend. */
export const getCloudBackend = (state: StoreState) => state.cloudBackend;

/** Returns a sorted list of app clients. */
export const selectClientList = createSelector([(state: StoreState) => state.clientList], (clientList) => clientList ? naturalSort(clientList, 'userName') : clientList);

/** Returns true if a valid help document is available, false otherwise. */
export const selectIsHelpDocumentAvailable = createSelector([(state: StoreState) => state.helpDocument], (helpDocument) => !!helpDocument);

/** Returns app deployment info (i.e. config.json) */
export const getAppDeploymentInfo = (state: StoreState) => state.appDeploymentInfo;

/** Returns true if the page layout should be rendered inside a containing frame (i.e. cockpit), false if not, and undefined if unknown (i.e. if config is not yet loaded). */
export const selectIsLayoutInsideFrame = createSelector([(state: StoreState) => state.appConfig], (appConfig) => appConfig?.isInsideFrame);

/** Returns current app config. */
export const getAppConfig = (state: StoreState) => state.appConfig;

/** Returns current app client, if any. */
export const getCurrentAppClient = (state: StoreState) => state.currentAppClient;


/** MODEL CUSTOMIZATION */

// for all supported createEntityAdapter selectors see https://redux-toolkit.js.org/api/createEntityAdapter#selector-functions

export const selectModels = (state: StoreState) => modelAdapterSelectors.selectAll(state);
export const selectModelById = (state: StoreState, id: string) => modelAdapterSelectors.selectById(state, id);
export const selectModelEntities = (state: StoreState) => modelAdapterSelectors.selectEntities(state);
export const selectModelIds = (state: StoreState) => modelAdapterSelectors.selectIds(state);
export const selectModelsInAlphabeticalOrder = createSelector([(state: StoreState) => selectModels(state)], models => naturalSort(models, 'modelName'));

export const selectCustomizationBases = (state: StoreState) => customizationBaseAdapterSelectors.selectAll(state);
export const selectCustomizationBaseIds = (state: StoreState) => customizationBaseAdapterSelectors.selectIds(state);
export const selectCustomizationBaseById = (state: StoreState, id: string) => customizationBaseAdapterSelectors.selectById(state, id);
export const selectCustomizationBaseEntities = (state: StoreState) => customizationBaseAdapterSelectors.selectEntities(state);
export const selectCustomizationBasesInAlphabeticalOrder = createSelector([(state: StoreState) => selectCustomizationBases(state)], bases => naturalSort(bases, 'customizationName'));

export const selectCustomizationOutputs = (state: StoreState) => customizationOutputAdapterSelectors.selectAll(state);
export const selectCustomizationOutputIds = (state: StoreState) => customizationOutputAdapterSelectors.selectIds(state);
export const selectCustomizationOutputEntities = (state: StoreState) => customizationOutputAdapterSelectors.selectEntities(state);
export const selectCustomizationOutputById = (state: StoreState, id: string) => customizationOutputAdapterSelectors.selectById(state, id);

export const selectModelCustomizationMetadata = (state: StoreState) => modelCustomizationsMetadataAdapterSelectors.selectAll(state);
export const selectModelCustomizationMetadataById = (state: StoreState, id: string) => modelCustomizationsMetadataAdapterSelectors.selectById(state, id);
export const selectModelCustomizationMetadataEntities = (state: StoreState) => modelCustomizationsMetadataAdapterSelectors.selectEntities(state);
export const selectOriginalModelCustomizationMetadataById = (state: StoreState, id: string) => originalCustomizationsMetadataAdapterSelectors.selectById(state, id);

export const selectRoiCustomizations = (state: StoreState) => roiCustomizationAdapterSelectors.selectAll(state);
export const selectRoiCustomizationById = (state: StoreState, id: string) => roiCustomizationAdapterSelectors.selectById(state, id);
export const selectRoiCustomizationEntities = (state: StoreState) => roiCustomizationAdapterSelectors.selectEntities(state);
export const selectOriginalRoiCustomizationById = (state: StoreState, id: string) => originalRoiCustomizationAdapterSelectors.selectById(state, id);

export const selectGlobalRoiCustomizations = (state: StoreState) => globalRoiCustomizationAdapterSelectors.selectAll(state);
export const selectGlobalRoiCustomizationById = (state: StoreState, id: string) => globalRoiCustomizationAdapterSelectors.selectById(state, id);
export const selectGlobalRoiCustomizationIds = (state: StoreState) => globalRoiCustomizationAdapterSelectors.selectIds(state);

export const selectCustomizationValidationError = (state: StoreState, id: string) => customizationValidationErrorAdapterSelectors.selectById(state, id);
export const selectCustomizationValidationErrorEntities = (state: StoreState) => customizationValidationErrorAdapterSelectors.selectEntities(state);

export const selectDaemonValidationError = (state: StoreState, id: string) => daemonValidationErrorAdapterSelectors.selectById(state, id);
export const selectDaemonValidationErrorEntities = (state: StoreState) => daemonValidationErrorAdapterSelectors.selectEntities(state);


export const selectAllCustomizationNamesAndParentModelIds = createSelector([selectCustomizationBases], (customizations) => customizations.map(c => ({ customizationName: c.customizationName, modelId: c.segmentationModelId })));

/** Factory function for making a memoized selector that returns all roi customization objects that belong to a specific customization model. */
export const makeSelectRoiCustomizationsForOutput = (): SelectorWithInputArg<RoiCustomization[], string> => createSelector(
    [(state: StoreState) => selectRoiCustomizations(state), (state: StoreState, customizationOutputId) => customizationOutputId],
    (roiCustomizations, customizationOutputId) => roiCustomizations.filter(r => r.modelCustomizationOutputId === customizationOutputId)
);

/** Factory function for making a memoized selector that returns true if any model customization object has been modified. */
export const selectIsAnyCustomizationModelModified = createSelector([selectModels], (models) => models.some(m => m.isModified));

export const makeSelectIsAnyMetadataForOutputModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    // [(state: StoreState) => selectModelCustomizationMetadata(state), (state: StoreState, customizationOutputId) => customizationOutputId],
    // (metadata, customizationOutputId) => metadata.filter(m => m.modelCustomizationOutputId === customizationOutputId).some(r => r.isModified)
    [(state: StoreState, customizationOutputId) => selectCustomizationOutputById(state, customizationOutputId), (state: StoreState) => selectModelCustomizationMetadataEntities(state)],
    (customizationOutput, metadataEntities) => customizationOutput?.metadata.some(mId => metadataEntities[mId]?.isModified) || false
);

export const makeSelectIsAnyRoiForOutputModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [(state: StoreState, customizationOutputId) => selectCustomizationOutputById(state, customizationOutputId), (state: StoreState) => selectRoiCustomizationEntities(state)],
    (customizationOutput, roiCustomizationEntities) => customizationOutput?.rois.some(rId => roiCustomizationEntities[rId]?.isModified) || false
);

export const makeSelectIsAnyTriggerForBaseModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [(state: StoreState, customizationBaseId) => selectCustomizationBaseById(state, customizationBaseId), (state: StoreState) => selectAeTitleRuleEntities(state), (state: StoreState) => selectDicomRuleEntities(state)],
    (customizationBase, aeTitleRuleEntities, dicomRuleEntities) => {
        const isAnyAeTitleRuleModified = customizationBase?.aeTitleRules.some(aId => aeTitleRuleEntities[aId]?.isModified) || false;
        if (isAnyAeTitleRuleModified) { return true; }
        return customizationBase?.dicomRules.some(dId => dicomRuleEntities[dId]?.isModified === true) || false;
    }
);


/** Generate a tree view hierarchy of models and some of their child objects. This is used by the customization page side navigation. */
export const selectCustomizationTreeViewItems = createSelector([
    (state: StoreState) => selectModelsInAlphabeticalOrder(state),
    (state: StoreState) => selectCustomizationBasesInAlphabeticalOrder(state),
    (state: StoreState) => selectCustomizationOutputEntities(state),
    (state: StoreState) => selectModelCustomizationMetadataEntities(state),
    (state: StoreState) => selectCustomizationValidationErrorEntities(state),

],
    (models, customizationBases, outputEntities, metadataEntities, validationEntities) => {
        const bodyPartNodes: CustomizationTreeViewDataItem[] = [];

        for (const model of models) {

            // create body part parent node if it doesn't exist yet
            let bodyPartNode = bodyPartNodes.find(n => n.label === model.bodyPart);
            if (bodyPartNode === undefined) {
                bodyPartNode = {
                    label: model.bodyPart,
                    children: [],
                    id: model.bodyPart,
                    isValid: true,
                    parentId: undefined,
                    type: CustomizationObjectType.None,
                    isModified: false,
                    isAvailable: true
                };
                bodyPartNodes.push(bodyPartNode);
            }

            const modelNode: CustomizationTreeViewDataItem = {
                label: model.label,
                id: model.id,
                children: [],
                type: CustomizationObjectType.Model,
                isModified: model.isModified,
                parentId: bodyPartNode.id,
                isValid: !validationEntities[model.id] || validationEntities[model.id]!.type !== CustomizationObjectType.Model,
                isAvailable: model.isAvailable
            };

            bodyPartNode.children.push(modelNode);

            for (const customizationBase of customizationBases.filter(b => model.customizations.includes(b.id))) {
                const baseNode: CustomizationTreeViewDataItem = {
                    label: customizationBase.customizationName,
                    id: customizationBase.id,
                    children: [],
                    type: CustomizationObjectType.CustomizationBase,
                    isModified: customizationBase.isModified,
                    parentId: modelNode.id,
                    isValid: (!validationEntities[customizationBase.id] || validationEntities[customizationBase.id]!.type !== CustomizationObjectType.CustomizationBase)
                        && customizationBase.aeTitleRules.every(aeId => !validationEntities[aeId] || validationEntities[aeId]!.type !== CustomizationObjectType.AeTitleRule)
                        && customizationBase.dicomRules.every(dId => !validationEntities[dId] || validationEntities[dId]!.type !== CustomizationObjectType.DicomRule),
                    isAvailable: model.isAvailable
                };
                modelNode.children.push(baseNode);

                if (customizationBase.outputs.length > 0) {
                    baseNode.children = [];
                    const outputs = customizationBase.outputs.map(oId => outputEntities[oId]);
                    assertArrayIsDefined(outputs);
                    for (const output of outputs) {
                        const metadata = output.metadata.map(mId => metadataEntities[mId]);
                        const filename = metadata.find(m => m?.attribute === METADATA_FILENAME_ATTRIBUTE)?.value || DEFAULT_FILENAME;
                        const outputNode: CustomizationTreeViewDataItem = {
                            label: filename,
                            id: output.id,
                            children: [],
                            type: CustomizationObjectType.CustomizationOutput,
                            isModified: output.isModified,
                            parentId: baseNode.id,
                            isValid: (!validationEntities[output.id] || validationEntities[output.id]!.type !== CustomizationObjectType.CustomizationOutput)
                                && output.metadata.every(mId => !validationEntities[mId] || validationEntities[mId]!.type !== CustomizationObjectType.Metadata)
                                && output.rois.every(rId => !validationEntities[rId] || validationEntities[rId]!.type !== CustomizationObjectType.Roi),
                            isAvailable: model.isAvailable
                        };
                        baseNode.children.push(outputNode);
                        baseNode.isValid = !outputNode.isValid ? false : baseNode.isValid;
                    }
                }

                modelNode.isValid = !baseNode.isValid ? false : modelNode.isValid;
            }
        }

        // set modified & valid settings for top-level body-part nodes
        for (const bodyPartNode of bodyPartNodes) {
            bodyPartNode.isValid = bodyPartNode.children.every(m => m.isValid);
            bodyPartNode.isModified = bodyPartNode.children.some(m => m.isModified);
        }

        return naturalSortInPlace(bodyPartNodes, 'label');
    });

// const walkThrougHierarchy = (item: CustomizationTreeViewDataItem, flatHierarchy: { [id: string]: CustomizationTreeViewDataItem }) => {
//     flatHierarchy[item.id] = item;
//     item.children.forEach(i => walkThrougHierarchy(i, flatHierarchy));
// }

// /** Generates a flat version of tree view hierarchy of models and some of their child objects */
// export const selectFlatCustomizationTreeViewItems = createSelector([(state: StoreState) => selectCustomizationTreeViewItems(state)], hierarchy => {
//     const flatHierarchy: { [id: string]: CustomizationTreeViewDataItem } = {};
//     hierarchy.forEach(r => walkThrougHierarchy(r, flatHierarchy));
//     return flatHierarchy;
// });

/** MODEL SELECTION */

// for all supported createEntityAdapter selectors see https://redux-toolkit.js.org/api/createEntityAdapter#selector-functions

export const selectAeTitleRules = (state: StoreState) => aeTitleRuleAdapterSelectors.selectAll(state);
export const selectAeTitleRuleEntities = (state: StoreState) => aeTitleRuleAdapterSelectors.selectEntities(state);
export const selectAeTitleRuleById = (state: StoreState, id: string) => aeTitleRuleAdapterSelectors.selectById(state, id);

export const selectDicomRules = (state: StoreState) => dicomRuleAdapterSelectors.selectAll(state);
export const selectDicomRuleEntities = (state: StoreState) => dicomRuleAdapterSelectors.selectEntities(state);
export const selectDicomRuleById = (state: StoreState, id: string) => dicomRuleAdapterSelectors.selectById(state, id);

export const selectDicomAttributeRules = (state: StoreState) => dicomAttributeRuleAdapterSelectors.selectAll(state);
export const selectDicomAttributeRuleById = (state: StoreState, id: string) => dicomAttributeRuleAdapterSelectors.selectById(state, id);


export type CustomizationBaseWithModelName = { customizationBaseId: string, label: string };
export const selectCustomizationBasesInNameOrder = createSelector([selectCustomizationBases, selectModelEntities],
    (customizations, modelEntities) => {
        const customizationNamesWithIds: CustomizationBaseWithModelName[] = customizations.map(c => ({
            customizationBaseId: c.id,
            label: `${modelEntities[c.segmentationModelId]?.label || ''} ${c.customizationName}`
        }));
        return naturalSort(customizationNamesWithIds, 'label');
    });


/** Returns true if all model selection rules are valid, false if any of them is invalid. */
export const selectIsEverySelectionRuleValid = createSelector([selectAeTitleRules, selectDicomRules, selectDicomAttributeRules],
    (aeTitleRules, dicomRules, dicomAttributeRules) => aeTitleRules.every(r => r.isValid)
        && dicomRules.every(r => r.isValid)
        && dicomAttributeRules.every(r => r.isValid));



/** Returns true if there are ANY unsaved changes in the app. */
// export const selectAnyUnsavedChangesInApp = createSelector([selectIsAnySelectionRuleModified], isAnyModelSelectionRuleModified => isAnyModelSelectionRuleModified);
export const selectAnyUnsavedChangesInApp = createSelector([selectIsAnyCustomizationModelModified], isAnyCustomizationModelModified => isAnyCustomizationModelModified);
